var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// x-tinymce.ts
import { acss } from "@adornis/chemistry/directives/acss.js";
import "@adornis/chemistry/elements/components/x-flex.js";
import "@adornis/chemistry/elements/components/x-loader.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import { AdornisFile } from "@adornis/file-utils/db/files.js";
import { FormField } from "@adornis/formfield/form-field.js";
import { html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ref } from "lit/directives/ref.js";
import { BehaviorSubject, distinctUntilChanged, filter, firstValueFrom, takeUntil, timer } from "rxjs";
var allTinyMCEPlugins = [
  "preview",
  "importcss",
  "searchreplace",
  "autolink",
  "directionality",
  "code",
  "visualblocks",
  "visualchars",
  "fullscreen",
  "image",
  "link",
  "media",
  "template",
  "codesample",
  "table",
  "charmap",
  "pagebreak",
  "nonbreaking",
  "anchor",
  "insertdatetime",
  "paste",
  "advlist",
  "lists",
  "wordcount",
  "help",
  "charmap",
  "quickbars",
  "emoticons",
  "autoresize"
];
var XTinyMCE = class extends FormField {
  constructor() {
    super(...arguments);
    this.customPlugins = [];
  }
  styles() {
    return [
      {
        "@keyframes loading": {
          "100%": {
            transform: "translateX(100%)"
          }
        },
        ":host": {
          display: "block",
          position: "relative",
          // avoid height jumping while initializing
          height: !!this._editor ? "" : "222px"
        },
        ".tox-tinymce": {
          // show tinymce as soon as it's initialized
          opacity: !!this._editor ? "1" : "0",
          "&:hover": { background: this.colors.neutral.shade(100) }
        },
        textarea: { opacity: "0" }
      }
    ];
  }
  static async ensureTinyMCE() {
    if (this._tinyMCEScript)
      return firstValueFrom(this._isTinyLoaded.pipe(filter(Boolean)));
    return new Promise((resolve, reject) => {
      this._tinyMCEScript = document.createElement("script");
      this._tinyMCEScript.src = "/__tinymce/tinymce.min.js";
      this._tinyMCEScript.referrerPolicy = "origin";
      this._tinyMCEScript.onerror = () => {
        console.log(
          "TinyMCE cannot be loaded from self-hosted endpoint. Falling back to cloud version. If this is intended, pass the api-key. If unintended and you want to self host, do server.use(selfHostedTinyMCE());"
        );
        reject();
      };
      this._tinyMCEScript.onload = () => {
        this._isTinyLoaded.next(true);
        resolve();
      };
      document.head.appendChild(this._tinyMCEScript);
    });
  }
  connectedCallback() {
    super.connectedCallback();
    this.value.observable.pipe(
      distinctUntilChanged(),
      filter((val) => val !== void 0),
      takeUntil(this.disconnected)
    ).subscribe((value) => this._editor?.setContent(value ?? ""));
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this._deinitTiny();
  }
  render() {
    return xComponents(html`
      <x-flex space="sm" h100>
        ${this.error ? html` <x-text tone="error"> ${this.error} </x-text> ` : nothing}
        <textarea
          ${ref((textarea) => this._inittiny(textarea))}
          .value=${this.value.value ?? ""}
        ></textarea>
      </x-flex>
      ${this._loader()}
    `);
  }
  _loader() {
    return html`
      <x-flex
        ${acss({
      position: "absolute",
      top: "0",
      left: "0",
      height: "100%",
      width: "100%",
      background: this.colors.neutral.shade(200),
      borderRadius: this.sizes.borderRadius,
      overflow: "hidden",
      display: !!this._editor ? "none" : "",
      "&::after": {
        display: "block",
        content: "''",
        position: "absolute",
        top: "0",
        width: "100%",
        height: "100%",
        transform: "translateX(-100%)",
        background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, .5), transparent)",
        animation: "loading 1.5s infinite"
      }
    })}
      ></x-flex>
    `;
  }
  async _inittiny(textarea) {
    if (!textarea || !!this._editor)
      return;
    await this.constructor.ensureTinyMCE();
    const retryIntervals = [10, 20, 50, 100, 200, 500, 1e3];
    let currentRetryPause = -1;
    while (!this._editor && ++currentRetryPause < retryIntervals.length - 1) {
      await firstValueFrom(timer(retryIntervals[currentRetryPause]));
      await new Promise(
        (resolve) => requestAnimationFrame(async () => {
          setTimeout(() => resolve(), 100);
          if (this._editor)
            return;
          for (const plugin of this.customPlugins) {
            if (tinymce.PluginManager.get(plugin.name)) {
              console.debug(
                "Skip adding tinymce plugin",
                plugin.name,
                "because a plugin with the same name is already registered"
              );
              continue;
            }
            console.debug("Adding tinymce plugin", plugin.name);
            tinymce.PluginManager.add(plugin.name, plugin.setup);
          }
          const editorOptions = {
            toolbar: "undo redo | bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | outdent indent lineheight |  numlist bullist | forecolor backcolor removeformat | charmap emoticons | fullscreen | insertfile image media template link anchor codesample | ltr rtl | table tablemergecells",
            toolbar_mode: "wrap",
            menubar: false,
            // @ts-expect-error https://www.tiny.cloud/docs/tinymce/6/contextmenu/
            contextmenu: false,
            quickbars_insert_toolbar: false,
            quickbars_selection_toolbar: false,
            elementpath: false,
            font_size_formats: "8pt 10pt 12pt 14pt 18pt 24pt 36pt",
            language: "de",
            plugins: allTinyMCEPlugins,
            setup: (editor) => {
              editor.on("blur", () => {
                this.value.next(editor.getContent());
                this.dispatchEvent(new CustomEvent("value-picked", { detail: { value: this.value.value } }));
              });
            },
            images_upload_handler: async (blobInfo, progress) => {
              const blob = blobInfo.blob();
              const id = await AdornisFile.upload(
                new File([blob], blobInfo.filename(), { lastModified: blob.lastModified, type: blob.type })
              );
              return AdornisFile.getServeLink(id);
            },
            ...this.editorOptions,
            placeholder: this.placeholder ?? "",
            target: textarea
          };
          const instances = await tinymce.init(editorOptions);
          this._editor = instances?.[0];
          if (this._editor)
            this.requestUpdate();
          resolve();
        })
      );
    }
  }
  _deinitTiny() {
    if (this._editor) {
      this._editor.remove();
      this._editor = void 0;
    }
  }
};
XTinyMCE._isTinyLoaded = new BehaviorSubject(false);
__decorateClass([
  property()
], XTinyMCE.prototype, "placeholder", 2);
__decorateClass([
  property({ attribute: false })
], XTinyMCE.prototype, "editorOptions", 2);
__decorateClass([
  property({ attribute: false })
], XTinyMCE.prototype, "customPlugins", 2);
XTinyMCE = __decorateClass([
  customElement("x-tinymce")
], XTinyMCE);
export {
  XTinyMCE,
  allTinyMCEPlugins
};
